import React, { useState, useEffect } from "react";
import { Box } from '@mui/material';
import ReportToolbar from "../ReportToolbar";
import ReportFooter from "../ReportFooter";
import { ProgramFilter, ReportFilterBar } from "../ReportFilters";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { DataGridPro } from '@mui/x-data-grid-pro';
import moment from "moment";
import axios from "axios";
import { APIURL } from "../../../config";
import dayjs from "dayjs";

const shortcutsItems = [
    {
        label: 'This Week',
        getValue: () => {
            const today = dayjs();
            return [today.startOf('week'), today.endOf('week')];
        },
    },
    {
        label: 'Last Week',
        getValue: () => {
            const today = dayjs();
            const prevWeek = today.subtract(7, 'day');
            return [prevWeek.startOf('week'), prevWeek.endOf('week')];
        },
    },
    {
        label: 'Last 7 Days',
        getValue: () => {
            const today = dayjs();
            return [today.subtract(7, 'day'), today];
        },
    },
    {
        label: 'Current Month',
        getValue: () => {
            const today = dayjs();
            return [today.startOf('month'), today.endOf('month')];
        },
    },
    {
        label: 'Last Month',
        getValue: () => {
            const today = dayjs();
            const lastMonth = today.subtract(1, 'month');
            return [lastMonth.startOf('month'), lastMonth.endOf('month')];
        },
    },
];

export default function ShiftChangeLogsReport(props) {
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState([]);
    const [selectedProgram, setSelectedProgram] = useState(null);
    const { startDate, endDate, setStartDate, setEndDate } = props;

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setIsLoading(true);

        axios
            .get(APIURL + "/reports/getShiftChangeLogsReport", {
                params: {
                    compID: props.user.comp_id,
                    empID: props.user.emp_id,
                    empType: props.user.type,
                    start: moment(startDate).startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
                    end: moment(endDate).endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
                    program_id: selectedProgram || undefined,
                },
            })
            .then((response) => {
                setData(response?.data.length !== 0 ? response?.data : []);
            })
            .catch((err) => {
                console.log(err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const rows = data.map((item, idx) => {
        return {
            id: idx,
            schedule_id: item.schedule_id,
            schedule_name: item.schedule_name || '',
            schedule_date: item.schedule_date ? moment.utc(item.schedule_date).format('MM/DD/YYYY') : '',
            shift_emp_name: item.shift_emp_first_name ? item.shift_emp_first_name + ' ' + item.shift_emp_last_name : '',
            action: item.action,
            notes: item.notes || '',
            action_by_name: item.action_by_first_name ? item.action_by_first_name + ' ' + item.action_by_last_name : '',
            action_at: moment(item.action_at).format('MM/DD/YYYY hh:mm A'),
        };
    });

    const columns = [
        { field: "schedule_date", headerName: "Schedule Date", width: 150 },
        // { field: "shift_emp_name", headerName: "Shift Employee", width: 200 },
        { field: "action", headerName: "Action", width: 200 },
        { field: "notes", headerName: "Notes", width: 300 },
        { field: "action_by_name", headerName: "Action By", width: 200 },
        { field: "action_at", headerName: "Action Time", width: 180 },
    ];

    const CustomToolbar = () => {
        return (
            <ReportToolbar companyName={props.user.company_name} reportTitle="Shift Change Logs Report">
                <Box sx={{ pl: 1, pr: 1 }}>
                    <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                        Date Range: {moment(startDate).format('MM/DD/YYYY')} - {moment(endDate).format('MM/DD/YYYY')}
                    </div>
                </Box>
            </ReportToolbar>
        );
    };

    return (
        <div style={{ width: '100%', padding: 10 }}>
            <div style={{ display: 'flex', marginBottom: 10, justifyContent: 'space-between' }}>
                <ReportFilterBar onSearch={() => fetchData()} style={{ flexWrap: 'wrap', rowGap: 10 }}>
                    <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        localeText={{ start: "From Date", end: "To Date" }}
                    >
                        <DateRangePicker
                            label='Date Range'
                            value={[
                                startDate ? dayjs(startDate) : null,
                                endDate ? dayjs(endDate) : null,
                            ]}
                            onChange={(newValue) => {
                                setStartDate(newValue[0] ? new Date(newValue[0]) : null);
                                setEndDate(newValue[1] ? new Date(newValue[1]) : null);
                            }}
                            slotProps={{
                                shortcuts: {
                                    items: shortcutsItems,
                                },
                                textField: { size: 'small', style: { width: 275 } }
                            }}
                        />
                    </LocalizationProvider>

                    <ProgramFilter
                        comp_id={props.user.comp_id}
                        value={selectedProgram}
                        onChange={setSelectedProgram}
                    />
                </ReportFilterBar>
            </div>
            <div style={{ height: 'calc(100vh - 250px)' }}>
                <DataGridPro
                    getRowId={(row) => row.id}
                    rows={rows}
                    columns={columns}
                    checkboxSelection={false}
                    disableRowSelectionOnClick
                    showToolbar
                    slots={{
                        toolbar: CustomToolbar,
                        footer: ReportFooter
                    }}
                    loading={isLoading}
                    treeData
                    getTreeDataPath={(row) => [row.schedule_name || `Schedule ${row.schedule_id}`, `${row.id}`]}
                    groupingColDef={{ headerName: 'Schedule' }}
                    sx={{
                        '& .MuiDataGrid-columnHeaderTitle': {
                            whiteSpace: 'normal',
                            lineHeight: 1.2,
                            textAlign: 'left',
                            wordBreak: 'break-word',
                            maxWidth: '100%',
                        },
                    }}
                />
            </div>
        </div>
    );
}
